import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "areaFrame",
    "areaButton",
    "areaTitle",
    "areaSubtitle",
    "item",
    "itemBody",
    "resultFrame",
    "statusPill",
    "progress",
    "progressCount",
    "progressBar",
    "photoPanel",
    "photoPanelTitle",
    "photoPanelBody",
  ]

  connect() {
    this.photoResultId = null
    this.boundKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown)
  }

  get selectedAreaKey() {
    return this.element.dataset.selectedAreaKey || ""
  }

  set selectedAreaKey(value) {
    this.element.dataset.selectedAreaKey = value || ""
  }

  selectArea(event) {
    event.preventDefault()
    const button = event.currentTarget
    const areaKey = button.dataset.areaKey
    const areaUrl = button.dataset.areaUrl
    if (!areaUrl || !this.hasAreaFrameTarget) return

    this.selectedAreaKey = areaKey
    this.areaFrameTarget.src = areaUrl

    this.areaButtonTargets.forEach((el) => {
      el.classList.toggle("is-active", el.dataset.areaKey === areaKey)
    })

    if (this.hasAreaTitleTarget) {
      this.areaTitleTarget.textContent = button.dataset.areaName || ""
    }
    if (this.hasAreaSubtitleTarget) {
      const total = button.dataset.areaTotal
      this.areaSubtitleTarget.textContent = total
        ? `${total} item${total === "1" ? "" : "s"} to review`
        : ""
    }

    this.updateAreaNavControls()
  }

  previousArea(event) {
    event.preventDefault()
    this.moveArea(-1)
  }

  nextArea(event) {
    event.preventDefault()
    this.moveArea(1)
  }

  moveArea(delta) {
    const keys = this.areaButtonTargets
      .map((el) => el.dataset.areaKey)
      .filter((key, index, arr) => arr.indexOf(key) === index)
    const currentIndex = keys.indexOf(this.selectedAreaKey)
    const nextKey = keys[currentIndex + delta]
    if (!nextKey) return

    const button = this.areaButtonTargets.find((el) => el.dataset.areaKey === nextKey)
    if (button) button.click()
  }

  updateAreaNavControls() {
    const keys = this.areaButtonTargets
      .map((el) => el.dataset.areaKey)
      .filter((key, index, arr) => arr.indexOf(key) === index)
    const index = keys.indexOf(this.selectedAreaKey)
    const prevBtn = this.element.querySelector('[data-action*="previousArea"]')
    const nextBtn = this.element.querySelector('[data-action*="nextArea"]')
    if (prevBtn) prevBtn.disabled = index <= 0
    if (nextBtn) nextBtn.disabled = index < 0 || index >= keys.length - 1
  }

  toggleItem(event) {
    event.preventDefault()
    const item = event.currentTarget.closest(".inspection-item")
    if (!item) return

    const isExpanded = item.classList.contains("is-expanded")
    if (isExpanded) {
      item.classList.remove("is-expanded")
      return
    }

    const frame = item.querySelector("turbo-frame")
    if (frame && !frame.dataset.loaded && frame.dataset.resultUrl) {
      frame.addEventListener(
        "turbo:frame-load",
        () => {
          frame.dataset.loaded = "true"
          if (window.$) $(frame).find(".best_in_place").best_in_place()
        },
        { once: true }
      )
      frame.src = frame.dataset.resultUrl
    }

    item.classList.add("is-expanded")
  }

  uploadFile(event) {
    const id = event.currentTarget.dataset.id
    const input = this.element.querySelector(`.result-file-input-${id}`)
    if (input) input.click()
  }

  uploadFileFromPanel(event) {
    event.preventDefault()
    if (!this.photoResultId) return
    const input = this.element.querySelector(`.result-file-input-${this.photoResultId}`)
    if (input) input.click()
  }

  autoUploadFile(event) {
    const form = event.target.closest("form")
    if (!form) return

    // Responds with turbo_stream so Turbo applies stream actions instead of
    // navigating the surrounding result turbo-frame.
    form.requestSubmit()
  }

  updateCondition(event) {
    const target = event.currentTarget
    const resultId = target.dataset.id
    const value = target.dataset.value
    const item = target.closest(".inspection-item") || document.getElementById(`inspection-item-${resultId}`)
    const wasConditioned = item && item.dataset.conditioned === "true"

    this.element.querySelectorAll(`.condition-btn-${resultId}`).forEach((btn) => {
      btn.classList.remove("active")
    })
    target.classList.add("active")

    if (item) {
      item.dataset.conditioned = "true"
      item.dataset.condition = value
      const check = item.querySelector(".inspection-item__check")
      if (check) {
        check.classList.add("is-complete")
        check.innerHTML = '<i class="fa fa-check"></i>'
      }
      const pill = item.querySelector(".inspection-status-pill")
      if (pill) {
        pill.dataset.condition = value
        pill.textContent = value
      }
    }

    $.ajax({
      url: `/inspection_reports/${target.dataset.inspectionReportId}/inspection_report_results/${resultId}`,
      type: "PUT",
      data: {
        inspection_report_result: {
          condition: value,
        },
      },
      success: () => {
        if (!wasConditioned) {
          this.bumpProgress(1)
          this.bumpSelectedAreaComplete(1)
        }
      },
    })
  }

  bumpProgress(delta) {
    if (!this.hasProgressTarget) return
    const complete = parseInt(this.progressTarget.dataset.complete || "0", 10) + delta
    const total = parseInt(this.progressTarget.dataset.total || "0", 10)
    this.progressTarget.dataset.complete = String(complete)
    if (this.hasProgressCountTarget) {
      this.progressCountTarget.textContent = `${complete} of ${total}`
    }
    if (this.hasProgressBarTarget) {
      const percent = total === 0 ? 0 : Math.round((complete / total) * 100)
      this.progressBarTarget.style.width = `${percent}%`
    }
    const hint = this.progressTarget.querySelector(".inspection-progress__hint")
    if (hint) {
      const remaining = total - complete
      hint.textContent =
        remaining > 0
          ? `Complete ${remaining} item${remaining === 1 ? "" : "s"} to finish`
          : "All items reviewed"
    }
  }

  bumpSelectedAreaComplete(delta) {
    this.areaButtonTargets
      .filter((el) => el.dataset.areaKey === this.selectedAreaKey)
      .forEach((el) => {
        const complete = parseInt(el.dataset.areaComplete || "0", 10) + delta
        const total = parseInt(el.dataset.areaTotal || "0", 10)
        el.dataset.areaComplete = String(complete)
        const label = el.querySelector("[data-area-complete-label]")
        if (label) {
          label.textContent = label.textContent.includes("/")
            ? `${complete}/${total}`
            : `${complete} of ${total} complete`
        }
        const index = el.querySelector(".inspection-area-nav__index")
        if (index && complete === total && total > 0) {
          index.classList.add("is-complete")
          index.innerHTML = '<i class="fa fa-check"></i>'
        }
      })
  }

  openPhotoPanel(event) {
    event.preventDefault()
    const button = event.currentTarget
    const resultId = button.dataset.id
    const photosUrl = button.dataset.photosUrl
    this.photoResultId = resultId
    this.photoEditable = button.dataset.editable === "true"

    const item = document.getElementById(`inspection-item-${resultId}`)
    const name =
      (item &&
        item.querySelector(".inspection-item__name") &&
        item.querySelector(".inspection-item__name").textContent) ||
      "Item"
    if (this.hasPhotoPanelTitleTarget) {
      this.photoPanelTitleTarget.textContent = `${name} photos`
    }

    const subtitle = this.photoPanelTarget && this.photoPanelTarget.querySelector(".inspection-photo-panel__subtitle")
    if (subtitle) {
      subtitle.textContent = this.photoEditable
        ? "Add or review photos for this item"
        : "Review photos for this item"
    }

    const footer = this.photoPanelTarget && this.photoPanelTarget.querySelector(".inspection-photo-panel__footer")
    if (footer) footer.hidden = !this.photoEditable

    if (this.hasPhotoPanelBodyTarget) {
      this.photoPanelBodyTarget.innerHTML =
        '<div class="inspection-photo-panel__empty"><p class="text-muted mb-0">Loading photos…</p></div>'
    }

    if (this.hasPhotoPanelTarget) {
      this.photoPanelTarget.hidden = false
      document.body.classList.add("inspection-photo-panel-open")
    }

    if (photosUrl) this.loadPhotoPanel(photosUrl)
  }

  loadPhotoPanel(photosUrl) {
    if (!this.hasPhotoPanelBodyTarget || !photosUrl) return

    fetch(photosUrl, {
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Unable to load photos")
        return response.text()
      })
      .then((html) => {
        if (this.hasPhotoPanelBodyTarget) this.photoPanelBodyTarget.innerHTML = html
      })
      .catch(() => {
        if (this.hasPhotoPanelBodyTarget) {
          this.photoPanelBodyTarget.innerHTML =
            '<div class="inspection-photo-panel__empty"><p class="fw-bold">Unable to load photos</p><p class="text-muted mb-0">Please try again.</p></div>'
        }
      })
  }

  closePhotoPanel(event) {
    if (event) event.preventDefault()
    this.photoResultId = null
    this.photoEditable = false
    if (this.hasPhotoPanelTarget) {
      this.photoPanelTarget.hidden = true
      document.body.classList.remove("inspection-photo-panel-open")
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.closePhotoPanel()
  }
}
